import { Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack, KeyEasing } from './anim-track';
import { Events } from './events';

type PictureEvidenceMetadata = {
    exhibitId?: string,
    capturedAt?: string,
    capturedBy?: string,
    device?: string,
    tags?: string[],
    description?: string
};

type Pose = PictureEvidenceMetadata & {
    name: string,
    frame: number,
    position: Vec3,
    target: Vec3,
    fov?: number,
    source?: string,
    validated?: boolean,
    easing?: KeyEasing
};

const sanitizeString = (value: unknown) => (typeof value === 'string' ? value : undefined);

const sanitizeTags = (value: unknown) => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    return value.filter((entry): entry is string => typeof entry === 'string');
};

const sanitizeBoolean = (value: unknown) => (typeof value === 'boolean' ? value : undefined);

const sanitizeNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

const sanitizeVec3 = (value: unknown) => {
    if (!Array.isArray(value) || value.length < 3) {
        return undefined;
    }
    const x = sanitizeNumber(value[0]);
    const y = sanitizeNumber(value[1]);
    const z = sanitizeNumber(value[2]);
    if (x === undefined || y === undefined || z === undefined) {
        return undefined;
    }
    return new Vec3(x, y, z);
};

const VALID_EASINGS = new Set(['linear', 'easeIn', 'easeOut', 'easeInOut', 'step']);

const sanitizeEasing = (value: unknown): KeyEasing | undefined => {
    return (typeof value === 'string' && VALID_EASINGS.has(value)) ? value as KeyEasing : undefined;
};

const sanitizePose = (value: unknown, fallbackFrame: number, fallbackFov?: number) => {
    const pose = (value && typeof value === 'object') ? value as any : {};
    const position = sanitizeVec3(pose.position);
    const target = sanitizeVec3(pose.target);

    return {
        name: sanitizeString(pose.name) ?? `camera_${fallbackFrame}`,
        frame: sanitizeNumber(pose.frame) ?? fallbackFrame,
        position: position ?? new Vec3(),
        target: target ?? new Vec3(),
        fov: sanitizeNumber(pose.fov) ?? fallbackFov,
        source: sanitizeString(pose.source),
        validated: sanitizeBoolean(pose.validated),
        exhibitId: sanitizeString(pose.exhibitId),
        capturedAt: sanitizeString(pose.capturedAt),
        capturedBy: sanitizeString(pose.capturedBy),
        device: sanitizeString(pose.device),
        tags: sanitizeTags(pose.tags),
        description: sanitizeString(pose.description),
        easing: sanitizeEasing(pose.easing)
    } as Pose;
};

/**
 * Camera animation track that manages camera keyframes and interpolation.
 * Implements AnimTrack interface so it can be used with the timeline system.
 *
 * Fully self-contained: subscribes to timeline events internally for
 * evaluation and spline rebuilding.
 */
class CameraAnimTrack implements AnimTrack {
    private poses: Pose[] = [];
    private events: Events;
    private sampleTimelinePose: ((frame: number, out: { position: Vec3; target: Vec3; fov?: number }) => void) | null = null;

    constructor(events: Events) {
        this.events = events;
    }

    get keys(): readonly number[] {
        return this.poses.map(p => p.frame);
    }

    addKey(frame: number): boolean {
        const pose = this.events.invoke('camera.getPose');
        if (!pose) return false;

        const existingIndex = this.poses.findIndex(p => p.frame === frame);

        const newPose: Pose = {
            name: `camera_${this.poses.length}`,
            frame,
            position: new Vec3(pose.position.x, pose.position.y, pose.position.z),
            target: new Vec3(pose.target.x, pose.target.y, pose.target.z),
            fov: pose.fov
        };

        if (existingIndex === -1) {
            this.poses.push(newPose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.poses[existingIndex] = newPose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.poses.findIndex(p => p.frame === frame);
        if (index === -1) return false;
        this.poses.splice(index, 1);
        this.rebuildSpline();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const index = this.poses.findIndex(p => p.frame === fromFrame);
        if (index === -1) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        // Update the frame (re-find index since splice may have shifted it)
        const movedIndex = this.poses.findIndex(p => p.frame === fromFrame);
        this.poses[movedIndex].frame = toFrame;
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const source = this.poses.find(p => p.frame === fromFrame);
        if (!source) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        this.poses.push({
            name: `camera_${this.poses.length}`,
            frame: toFrame,
            position: source.position.clone(),
            target: source.target.clone(),
            fov: source.fov
        });

        this.rebuildSpline();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    getKeyEasing(frame: number): KeyEasing | undefined {
        const pose = this.poses.find(p => p.frame === frame);
        return pose?.easing;
    }

    setKeyEasing(frame: number, easing: KeyEasing | undefined): boolean {
        const pose = this.poses.find(p => p.frame === frame);
        if (!pose) return false;
        if (pose.easing === easing) return false;
        pose.easing = easing;
        this.rebuildSpline();
        this.events.fire('track.keyUpdated', frame);
        return true;
    }

    evaluate(frame: number): void {
        this.samplePose(frame);
    }

    samplePose(frame: number): { position: Vec3; target: Vec3; fov?: number } | null {
        if (!this.sampleTimelinePose) {
            return null;
        }

        const pose = {
            position: new Vec3(),
            target: new Vec3(),
            fov: undefined as number | undefined
        };

        this.sampleTimelinePose(frame, pose);
        return pose;
    }

    clear(): void {
        this.poses.length = 0;
        this.sampleTimelinePose = null;
        this.events.fire('track.keysCleared');
    }

    snapshot(): Pose[] {
        return this.poses.map(p => ({
            name: p.name,
            frame: p.frame,
            position: p.position.clone(),
            target: p.target.clone(),
            fov: p.fov,
            source: p.source,
            validated: p.validated,
            exhibitId: p.exhibitId,
            capturedAt: p.capturedAt,
            capturedBy: p.capturedBy,
            device: p.device,
            tags: p.tags?.slice(),
            description: p.description,
            easing: p.easing
        }));
    }

    restore(snapshot: unknown): void {
        this.poses = (snapshot as Pose[]).map((p, index) => sanitizePose({
            ...p,
            position: [p.position?.x, p.position?.y, p.position?.z],
            target: [p.target?.x, p.target?.y, p.target?.z]
        }, index));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /**
     * Add a pose directly (used for deserialization and legacy import).
     */
    addPose(pose: Pose): void {
        if (pose.frame === undefined) {
            return;
        }

        pose.fov ??= this.events.invoke('camera.fov') ?? 60;

        const idx = this.poses.findIndex(p => p.frame === pose.frame);
        if (idx !== -1) {
            this.poses[idx] = pose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', pose.frame);
        } else {
            this.poses.push(pose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', pose.frame);
        }
    }

    /**
     * Get all poses (used for serialization and legacy consumers).
     */
    getPoses(): readonly Pose[] {
        return this.poses;
    }

    /**
     * Load poses from serialized data.
     */
    loadPoses(posesData: Pose[]): void {
        this.poses.length = 0;
        posesData.forEach((pose) => {
            this.poses.push(pose);
        });
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    rebuildSpline(): void {
        const duration = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');

        const orderedPoses = this.poses.slice()
        .filter(a => a.frame < duration)
        .sort((a, b) => a.frame - b.frame);

        const times = orderedPoses.map(p => p.frame);
        const points: number[] = [];
        for (let i = 0; i < orderedPoses.length; ++i) {
            const p = orderedPoses[i];
            points.push(p.position.x, p.position.y, p.position.z);
            points.push(p.target.x, p.target.y, p.target.z);
            points.push(p.fov);
        }

        if (orderedPoses.length > 1) {
            const easings = orderedPoses.map(p => p.easing);
            const spline = CubicSpline.fromPointsLooping(duration, times, points, smoothness, easings);
            const result: number[] = [];

            this.sampleTimelinePose = (frame: number, pose: { position: Vec3; target: Vec3; fov?: number }) => {
                spline.evaluate(frame, result);
                pose.position.set(result[0], result[1], result[2]);
                pose.target.set(result[3], result[4], result[5]);
                pose.fov = result[6];
            };
        } else {
            this.sampleTimelinePose = null;
        }

        this.events.fire('cameraPlayback.refresh');
    }
}

/**
 * Register the camera animation track and expose it via events.
 * The track is fully self-contained (subscribes to timeline events internally),
 * so this function only needs to create it, expose it, and handle serialization.
 */
const registerCameraPosesEvents = (events: Events) => {
    const track = new CameraAnimTrack(events);

    events.on('timeline.frames', () => {
        track.rebuildSpline();
    });

    events.on('timeline.smoothness', () => {
        track.rebuildSpline();
    });

    events.on('scene.clear', () => {
        track.clear();
        events.fire('cameraPlayback.refresh');
    });

    const firePoseChangeEvents = () => {
        const poses = track.getPoses();
        const needsValidation = poses.some(pose => pose.validated === false);
        events.fire('camera.posesChanged', poses);
        events.fire('camera.posesValidationChanged', needsValidation);
    };

    // Expose the camera animation track
    events.function('camera.animTrack', () => {
        return track;
    });

    events.function('camera.animTrack.samplePose', (frame: number) => {
        return track.samplePose(frame);
    });

    // Legacy support: expose poses
    events.function('camera.poses', () => {
        return track.getPoses();
    });

    events.function('camera.posesNeedsValidation', () => {
        const poses = track.getPoses();
        return poses.some(pose => pose.validated === false);
    });

    events.function('camera.validatePoses', (source?: string) => {
        const poses = track.getPoses();
        let updated = false;
        poses.forEach((pose) => {
            if (source && pose.source && pose.source !== source) {
                return;
            }
            if (pose.validated === false) {
                pose.validated = true;
                updated = true;
            }
        });
        if (updated) {
            firePoseChangeEvents();
        }
        return true;
    });

    // Legacy support: add pose directly
    events.on('camera.addPose', (pose: Pose) => {
        track.addPose(pose);
        firePoseChangeEvents();
    });

    // Serialization

    events.function('docSerialize.poseSets', (): any[] => {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const poses = track.getPoses();

        if (poses.length === 0) {
            return [];
        }

        return [{
            name: 'set0',
            poses: poses.map((pose) => {
                return {
                    name: pose.name,
                    frame: pose.frame,
                    position: pack3(pose.position),
                    target: pack3(pose.target),
                    fov: pose.fov,
                    source: pose.source,
                    validated: pose.validated,
                    exhibitId: pose.exhibitId,
                    capturedAt: pose.capturedAt,
                    capturedBy: pose.capturedBy,
                    device: pose.device,
                    tags: pose.tags?.slice(),
                    description: pose.description,
                    easing: pose.easing
                };
            })
        }];
    });

    events.function('docDeserialize.poseSets', (poseSets: any[], documentCameraFov?: number) => {
        if (!poseSets || poseSets.length === 0) {
            return;
        }

        const fps = events.invoke('timeline.frameRate');

        const defaultFov = documentCameraFov ?? events.invoke('camera.fov') ?? 60;

        const loadedPoses: Pose[] = poseSets[0].poses.map((docPose: any, index: number) => {
            return sanitizePose(docPose, (index * fps), defaultFov);
        });

        track.loadPoses(loadedPoses);
        firePoseChangeEvents();
    });

    events.on('camera.setPoses', (posesData: unknown[]) => {
        const loadedPoses = Array.isArray(posesData) ? posesData.map((pose, index) => sanitizePose(pose, index)) : [];
        track.loadPoses(loadedPoses);
        firePoseChangeEvents();
    });
};

export { registerCameraPosesEvents, CameraAnimTrack, Pose };
export type { PictureEvidenceMetadata };
